import { ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { ModuleLocked } from "@/components/ModuleLocked";
import type { ModuleKey } from "@/lib/modules";

interface Props {
  module: ModuleKey;
  children: ReactNode;
}

/**
 * Gates a route behind a per-organisation module toggle.
 *
 * Anonymous (signed-out) visitors always see the page so public marketing
 * surfaces continue to work. Signed-in members of an organisation that has
 * the module turned OFF (or, for assessment, expired) see the lock screen.
 */
export function ModuleGuard({ module, children }: Props) {
  const { user, isModuleEnabled, assessmentAccess, loading } = useAuth();

  if (loading) return <>{children}</>;
  if (!user) return <>{children}</>;
  if (isModuleEnabled(module)) return <>{children}</>;

  // For the assessment module, surface the expired variant when relevant.
  if (module === "assessment" && assessmentAccess.expired) {
    return <ModuleLocked module={module} expiredAt={assessmentAccess.expiresAt} />;
  }
  return <ModuleLocked module={module} />;
}

export default ModuleGuard;
